import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import UserSelectDialog from 'components/UserSelectDialog';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { dateFormatter, enum2NumberArray, getFullName } from 'core/utils/commonUtils';
import { getOrderPayoutTypeName } from 'core/utils/orderUtils';
import { decimalPlaces } from 'core/utils/validators/common';
import dayjs from 'dayjs';
import { gt, isNil } from 'lodash';
import { OrderPayoutTypeEnum } from 'models/enums/orderPayoutTypeEnum';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  FormContainer,
  SelectElement,
  TextareaAutosizeElement,
  TextFieldElement,
} from 'react-hook-form-mui';
import * as yup from 'yup';
import type { OrderPayoutInfo } from 'models/order/orderPayoutInfo';
import type { User } from 'models/user/user';
import type { FC } from 'react';

const datePayoutMaxDate = dayjs().endOf('day');

const schema = yup.object().shape({
  sum: decimalPlaces(2)
    .required()
    .min(0)
    .max(10_000_000)
    .notOneOf([0], 'Значение должно быть больше 0'),
  type: yup.number().required(),
  remark: yup.string(),
  userPayoutId: yup.number().optional().nonNullable(),
  userPayout: yup
    .object()
    .shape({
      id: yup.number().required(),
      fullName: yup.string().required(),
    })
    .nullable()
    .default(undefined),
  datePayout: yup.date().max(datePayoutMaxDate.toDate()).required(),
});

type FormData = yup.InferType<typeof schema>;

const PayoutEditDialog: FC<PayoutEditDialogProps> = ({
  payout,
  open,
  onCancel,
  onSave,
}) => {
  const formContext = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      datePayout: new Date(),
      sum: 0,
      type: OrderPayoutTypeEnum.Cash,
    },
  });

  const [saving, setSaving] = useState(false);
  const [openUserSelectDialog, setOpenUserSelectDialog] = useState(false);

  useEffect(() => {
    formContext.reset(payout);
  }, [formContext, payout]);

  const handleOnSave: VoidFunction = () => {
    const editedPayout: OrderPayoutInfo = {
      ...payout,
      ...formContext.getValues(),
      remark: formContext.getValues('remark') ?? '',
      datePayout: formContext.getValues('datePayout'),
      userPayout: formContext.getValues('userPayout') ?? undefined,
    };

    if (!isNil(onSave)) {
      setSaving(true);
      onSave(editedPayout)
        .finally(() => {
          setSaving(false);
        })
        .catch(console.log);
    }
  };

  const getTitle = (): string =>
    gt(payout.id, 0)
      ? `Выплата от ${dateFormatter.format(payout.dateCreate)}`
      : 'Новая выплата';

  const userSelectSaveHandler = (users: User[]): void => {
    setOpenUserSelectDialog(false);
    if (users.length > 0 && !isNil(users[0])) {
      onUserChange(users[0]);
    }
  };

  const onUserChange = (user: User): void => {
    formContext.setValue('userPayoutId', user.id);
    formContext.setValue('userPayout', {
      id: user.id,
      fullName: getFullName(user),
    });
  };

  const getSelectedUserName = (): string => {
    const user = formContext.getValues().userPayout;
    return !isNil(user) ? user.fullName : 'Не задан';
  };

  const handleOnCancel: VoidFunction = () => {
    formContext.reset(payout);
    onCancel?.();
  };

  return (
    <SimpleDialog
      title={getTitle()}
      open={open}
      onClose={handleOnCancel}
      actions={
        <>
          <LoadingButton
            loading={saving}
            onClick={() => {
              void formContext.handleSubmit(handleOnSave)();
            }}>
            <span>Сохранить</span>
          </LoadingButton>
          <Button onClick={handleOnCancel}>Закрыть</Button>
        </>
      }
      content={
        <>
          {!isNil(formContext.formState.errors) && (
            <Box sx={{ color: 'error.main', mt: 2, mb: 2 }}>
              {Object.entries(formContext.formState.errors).map(([key, error]) => (
                <Typography key={key} variant='body2'>
                  {JSON.stringify(error)}
                </Typography>
              ))}
            </Box>
          )}
          <FormContainer formContext={formContext} onSuccess={handleOnSave}>
            <Controller
              name={'datePayout'}
              control={formContext.control}
              render={({ fieldState: { error }, field: { onChange, value } }) => (
                <DatePicker
                  label={'Дата выплаты'}
                  value={!isNil(value) ? dayjs(value) : null}
                  onChange={(value, context) => {
                    onChange(value?.toDate(), context);
                  }}
                  maxDate={datePayoutMaxDate}
                  slotProps={{
                    textField: {
                      margin: 'normal',
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            <TextFieldElement
              fullWidth
              label='Сумма'
              name='sum'
              type='number'
              margin='normal'
              required
            />

            <SelectElement
              label='Тип'
              name='type'
              margin='normal'
              fullWidth
              required
              options={enum2NumberArray(OrderPayoutTypeEnum).map((v) => ({
                id: v,
                label: getOrderPayoutTypeName(v),
              }))}
            />

            <TextField
              fullWidth
              label='Получатель'
              margin='normal'
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      edge='end'
                      onClick={() => {
                        setOpenUserSelectDialog(true);
                      }}>
                      <EditIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={getSelectedUserName()}
            />

            <TextareaAutosizeElement
              fullWidth
              label='Примечание'
              name='remark'
              margin='normal'
              resizeStyle='vertical'
              rows={2}
            />
          </FormContainer>

          <UserSelectDialog
            open={openUserSelectDialog}
            onCancel={() => {
              setOpenUserSelectDialog(false);
            }}
            onSave={userSelectSaveHandler}
            description='Выберите получателя выплаты'
          />
        </>
      }
    />
  );
};

export interface PayoutEditDialogProps {
  payout: OrderPayoutInfo;
  open: boolean;
  onSave?: (payout: OrderPayoutInfo) => Promise<boolean>;
  onCancel?: VoidFunction;
}

export default PayoutEditDialog;
